import {Dropdown, Text, TextField, Toggle} from '@/components/atoms'
import {PromotionCondition} from '@/types/promotion'
import {Constants} from '@/utils/constants/commonConstants'
import {ANY_ID} from '@/utils/constants/promotionConstants'
import {DropdownItem} from 'pepsico-ds'
import {FC} from 'react'
import PromotionSelectionModal from '../PromotionSelectionModal'
import {RewardItem} from './Rewards/RewardItem'
import {BuyTarget, useBuySingleProductCondition} from './useBuySingleProductCondition'

type BuySingleProductConditionsProps = {
  promotionCondition: PromotionCondition
  index: number
  selectedPromotionCondition?: string
  ModalComponent?: React.ComponentType<any>
}

export const BuySingleProductConditions: FC<BuySingleProductConditionsProps> = ({
  promotionCondition,
  index,
  selectedPromotionCondition = 'UN',
  ModalComponent = PromotionSelectionModal,
}) => {
  const {
    isLoadingConfig,
    buyTypeOptions,
    buyQtyOptions,
    currentCondition,
    hasMinQuantity,
    isMinQuantityFeatureEnabled,
    mainView,
    minView,
    modalProps,
    isFirstCondition,
    isError,
    isMinQuantityError,
    isQuantityTab,
    currencyCode,
    textAreavalue,
    handleBuyCount,
    handleMinQuantity,
    handleToggleMinQuantity,
    handleBuyAmount,
    handleMinAmount,
    handleSelectedBuyItem,
    handleSelectedMinBuyItem,
    handleSubmitModal,
    handleCloseModal,
  } = useBuySingleProductCondition({promotionCondition, index, selectedPromotionCondition})

  const renderProductsDropdown = (
    target: BuyTarget,
    childList: {id: string; displayText: string}[],
    view: {selectedText: string; productsValue: {id: string; displayText: string}[]},
    onChange: (values: DropdownItem[]) => void
  ) => (
    <div className={index > 0 ? 'pointer-events-none' : ''}>
      <Dropdown
        isJsonWrap={false}
        childList={childList}
        placeholder={view.selectedText || Constants.AddProducts}
        value={[...view.productsValue]}
        updateValue={onChange}
        isClearable={true}
        // @ts-expect-error types
        autoComplete="off"
        loading={isLoadingConfig}
        required={target === 'main' && isFirstCondition}
        selection="multiple"
        disabled={index > 0 || (target === 'min' && !mainView.productsValue.length)}
      />
    </div>
  )

  return (
    <div className="flex flex-col gap-4">
      {index === 0 && isMinQuantityFeatureEnabled && (
        <Toggle
          value={hasMinQuantity}
          updateValue={handleToggleMinQuantity}
          uiSchema={{label: 'Enable Minimum Quantity'}}
        />
      )}
      <div className="flex flex-wrap items-baseline gap-4">
        <Text>Buy</Text>

        {isQuantityTab ? (
          <Dropdown
            isJsonWrap={false}
            childList={buyQtyOptions}
            placeholder={ANY_ID}
            updateValue={handleBuyCount}
            isClearable={false}
            value={mainView.quantityValue}
            selection="single"
            // @ts-expect-error types
            autoComplete="off"
            style={{width: 84}}
            loading={isLoadingConfig}
            required={isFirstCondition}
            isError={isError}
            errorMessage="Value Invalid"
          />
        ) : (
          <>
            <Text>{currencyCode}</Text>
            <TextField
              updateValue={handleBuyAmount}
              value={textAreavalue?.toString() || ''}
              error={isError ? 'Value Invalid' : ''}
              uiSchema={{
                helperText: '',
                placeholder: '0.0',
              }}
              // @ts-ignore
              style={{maxWidth: '85px'}}
              type="number"
            />
          </>
        )}

        {renderProductsDropdown('main', buyTypeOptions, mainView, handleSelectedBuyItem)}

        {hasMinQuantity && (
          <>
            <Text>, with min quantity of</Text>
            {isQuantityTab ? (
              <Dropdown
                isJsonWrap={false}
                childList={buyQtyOptions}
                placeholder={ANY_ID}
                updateValue={handleMinQuantity}
                isClearable={false}
                value={minView.quantityValue}
                selection="single"
                // @ts-expect-error types
                autoComplete="off"
                style={{width: 84}}
                loading={isLoadingConfig}
                isError={isMinQuantityError}
                errorMessage="Value Invalid"
              />
            ) : (
              <>
                <Text>{currencyCode}</Text>
                <TextField
                  updateValue={handleMinAmount}
                  value={minView.textValue}
                  error={isMinQuantityError ? 'Value Invalid' : ''}
                  uiSchema={{
                    helperText: '',
                    placeholder: '0.0',
                  }}
                  // @ts-ignore
                  style={{maxWidth: '85px'}}
                  type="number"
                />
              </>
            )}
            {renderProductsDropdown('min', mainView.productsValue, minView, handleSelectedMinBuyItem)}
          </>
        )}
        <Text>and get</Text>
        {/* @ts-ignore */}
        <RewardItem index={index} promotionCondition={promotionCondition} ModalComponent={ModalComponent} />
      </div>

      <ModalComponent
        key={JSON.stringify(modalProps.type)}
        show={modalProps.show}
        type={modalProps.type}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        conditionValues={{
          itemType: (modalProps.target === 'min' ? currentCondition?.minItemType : currentCondition?.itemType) || '',
          values: modalProps.target === 'min' ? minView.productsValue : mainView.productsValue,
          productCombination: currentCondition?.productCombination,
        }}
      />
    </div>
  )
}
